import React, { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { MaskedLines, MagneticButton, scrollToId } from "./primitives";
import { DOWNLOAD_URL } from "../../data/content";
import { useLanguage } from "../../context/LanguageContext";

const Mascot = lazy(() => import("./Mascot"));

export default function Hero() {
  const { lang, t } = useLanguage();
  const scrollTo = (id) => scrollToId(id);

  const heroLines = lang === "id"
    ? ["Masa Depan", "Modding", <>Ada <span className="text-primary">Disini.</span></>]
    : ["The Future", "Of Modding", <>is <span className="text-primary">Here.</span></>];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-20 sm:pt-24 md:pt-28 pb-8 sm:pb-12"
    >
      {/* backdrop layers */}
      <div className="absolute inset-0 tech-grid radial-fade opacity-[0.5] pointer-events-none" />
      <div
        className="absolute -top-40 -right-40 w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(59,91,255,0.16), transparent 62%)" }}
      />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7 order-1 pb-6 lg:pb-0">
          <h1
            data-testid="hero-headline"
            key={lang}
            className="font-display font-bold tracking-tighter text-white text-[2.75rem] xs:text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[1.04] sm:leading-[0.98]"
          >
            <MaskedLines
              lines={heroLines}
              lineClassName="pb-1"
            />
          </h1>

          <motion.p
            key={`subcopy-${lang}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-5 sm:mt-7 max-w-xl text-white/65 text-sm sm:text-base md:text-lg leading-relaxed"
            data-testid="hero-subcopy"
          >
            {t.hero.subcopy}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-7 sm:mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4"
          >
            <MagneticButton
              testId="hero-primary-cta"
              onClick={() => scrollTo(DOWNLOAD_URL)}
              className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:px-7 sm:py-4 bg-primary text-white font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.18em] hover:bg-white hover:text-black transition-colors duration-300 w-full sm:w-auto"
            >
              {t.hero.primaryCta}
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </MagneticButton>
            <MagneticButton
              testId="hero-secondary-cta"
              onClick={() => scrollTo("#about")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:px-7 sm:py-4 border border-white/15 text-white/80 font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.18em] hover:border-primary hover:text-white transition-colors duration-300 w-full sm:w-auto"
            >
              {t.hero.secondaryCta}
            </MagneticButton>
          </motion.div>
        </div>

        {/* RIGHT — 3D mascot */}
        <div className="lg:col-span-5 order-2 relative">
          <div
            data-testid="mascot-stage"
            className="relative w-full h-[32vh] sm:h-[45vh] lg:h-[76vh] max-h-[720px] flex items-center justify-center bg-transparent"
          >
            <Suspense fallback={<div className="w-full h-full bg-transparent" />}>
              <Mascot />
            </Suspense>
          </div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollTo("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="hidden lg:flex absolute bottom-7 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-white/40 hover:text-white transition-colors"
        data-testid="scroll-hint"
        aria-label="Scroll down"
      >
        <span className="font-mono-tech text-[10px] tracking-[0.3em]">SCROLL</span>
        <ChevronDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
